import { axiosRequest } from '~/src/services/api/axios-request';
import { TResponse } from '~/src/services/api/helper';
import { ICurrentProject, IImage } from '~/src/services/projects';
import { captureQueryError } from '@r1-frontend/do-request/error';

const URL_PATH = '/project';

const getParseData = (data: any) => {
    if (data.body && typeof data.body === 'string') {
        data.body = JSON.parse(data.body);
    }

    return data;
};

export type TListResponse = TResponse<ICurrentProject[], any>;

export const getProjectList = async (accessToken?: string): Promise<TListResponse> => {
    try {
        const resp = await axiosRequest(accessToken).get('/projects?filter[isPublic]=false');

        return {
            isSuccess: true,
            payload: resp.data,
        };
    } catch (err) {
        return captureQueryError(err);
    }
};

export type TProjectResponse = TResponse<ICurrentProject, any>;
export type TImageResponse = TResponse<IImage, any>;
export type TImagesResponse = TResponse<IImage[], any>;

export const getProject = async (accessToken: string, id: string): Promise<TProjectResponse> => {
    try {
        const resp = await axiosRequest(accessToken).get(`${URL_PATH}/${id}`);

        return {
            isSuccess: true,
            payload: getParseData(resp.data),
        };
    } catch (err) {
        return captureQueryError(err);
    }
};

export const getProjectByAlias = async (alias: string, isPreview = false): Promise<TProjectResponse> => {
    try {
        const url = isPreview ? `/project/preview/${alias}` : `landing/${alias}`;

        const resp = await axiosRequest().get(url);

        return {
            isSuccess: true,
            payload: getParseData(resp.data),
        };
    } catch (err) {
        return captureQueryError(err);
    }
};

export const createProject = async (accessToken: string, data: Partial<ICurrentProject>): Promise<TProjectResponse> => {
    try {
        const resp = await axiosRequest(accessToken).post(URL_PATH, (data));

        return {
            isSuccess: true,
            payload: getParseData(resp.data),
        };
    } catch (err) {
        return captureQueryError(err);
    }
};

export const updateProject = async (accessToken: string, id: string, data: Partial<ICurrentProject>): Promise<TProjectResponse> => {
    try {
        const resp = await axiosRequest(accessToken).put(`${URL_PATH}/${id}`, data);

        return {
            isSuccess: true,
            payload: getParseData(resp.data),
        };
    } catch (err) {
        return captureQueryError(err);
    }
};

export const publishProject = async (accessToken: string, id: string): Promise<TProjectResponse> => {
    try {
        const resp = await axiosRequest(accessToken).post(`${URL_PATH}/${id}/publish`);

        return {
            isSuccess: true,
            payload: getParseData(resp.data),
        };
    } catch (err) {
        return captureQueryError(err);
    }
};

export const unpublishProject = async (accessToken: string, id: string): Promise<TProjectResponse> => {
    try {
        const resp = await axiosRequest(accessToken).post(`${URL_PATH}/${id}/unpublish`);

        return {
            isSuccess: true,
            payload: getParseData(resp.data),
        };
    } catch (err) {
        return captureQueryError(err);
    }
};

export const deleteProject = async (accessToken: string, id: string): Promise<TProjectResponse> => {
    try {
        const resp = await axiosRequest(accessToken).delete(`${URL_PATH}/${id}`);

        return {
            isSuccess: true,
            payload: getParseData(resp),
        };
    } catch (err) {
        return captureQueryError(err);
    }
};

export const getImages = async (accessToken: string, id: string): Promise<TImagesResponse> => {
    try {
        const resp = await axiosRequest(accessToken).get(`${URL_PATH}/${id}/images`);

        return {
            isSuccess: true,
            payload: resp.data,
        };
    } catch (err) {
        return captureQueryError(err);
    }
};

export const uploadImage = async (accessToken: string, id: string, imageData: File): Promise<TImageResponse> => {
    try {
        const postData = new FormData();
        const headers = {
            'Content-Type': 'application/form-data',
        };

        postData.append('image', imageData);
        const result = await axiosRequest(accessToken, headers)
            .post(`${URL_PATH}/${id}/image`, postData);

        return {
            isSuccess: true,
            payload: result.data,
        };
    } catch (err) {
        return captureQueryError(err);
    }
};

const ENDPOINT = process.env.NEXT_PUBLIC_B2C_URL;
export type TCSRFTokenResponse = TResponse<{ token: string }, any>;

export const getCSRFToken = async (): Promise<TCSRFTokenResponse> => {
    try {
        const resp = await axiosRequest()
            .get(`${ENDPOINT}/api/csrf-token`);

        return {
            isSuccess: true,
            payload: resp.data,
        };
    } catch (err) {
        console.error(`CSRFToken ERROR: ${err.code || err.status || 'UNKNOWN_ERROR'}, ${err.message || 'Не содержит message'}`);

        return captureQueryError(err);
    }
};
